/**
 * Content Utils
 * this file contains utils used for getting complex or conditional content
 *
 * Naming Convention: get${Example_Content}Content
 * i.e. getExampleContent
 */

import type FlightInfoStore from "@tajawal/web-flights-sdk/lib/stores/flight-info.store";
import {
  ADDONS,
  AIRCRAFT_TYPE,
  CABIN_TYPES,
  DURATION_UNIT,
  FLYADEAL_CODE,
  INBOUND,
  OUTBOUND,
  LOCATION_TYPES,
  PERPIECEWEIGHTFLAG,
  SEARCH_BOX_CONSTANTS,
  SEARCH_TYPES,
  TRAVELLERS_TYPES_CODES_MAP,
} from "@tajawal/web-flights-sdk/lib/constants";
import { DATE_FORMATS } from "@/constants/date.constants";

// lodash
import _max from "lodash/max";
import _replace from "lodash/replace";
import _get from "lodash/get";
import _values from "lodash/values";
import _countBy from "lodash/countBy";
import _isNumber from "lodash/isNumber";
import _isPlainObject from "lodash/isPlainObject";
import _filter from "lodash/filter";
import _toLower from "lodash/toLower";
import _flatMap from "lodash/flatMap";
import _map from "lodash/map";
import _split from "lodash/split";
import _trim from "lodash/trim";
import _reduce from "lodash/reduce";
import _isNil from "lodash/isNull";
import _join from "lodash/join";
import _isEmpty from "lodash/isEmpty";
import _size from "lodash/size";
import { formatDate } from "@/utils/date.utils";
import { TFunction } from "i18next";
import { FormApi } from "final-form";
import { LuggageOptionModel } from "@tajawal/web-flights-sdk/lib/types/luggage.types";
import {
  BaggagePerTraveller,
  Contact,
  InsuranceBenefits,
  TravellerTitleLabel,
} from "@/types/common.types";
import { TravellerModel } from "@tajawal/web-flights-sdk/lib/types/traveller.types";
import {
  FlightDirection,
  PaxType,
  TravellerTitle,
} from "@tajawal/web-flights-sdk/lib/types/common.types";
import {
  BaggageInfoModel,
  LegBaggageInfo,
} from "@tajawal/web-flights-sdk/lib/types/baggage.types";
import {
  APP_PAGES,
  BRANDS,
  LOYALTY_METHODS,
} from "@tajawal/web-core-sdk-v2/lib/constants";
import {
  AIRCRAFT_TYPES,
  AIRLINE_IMAGE_URL,
  AUTH_STATE_MAP,
  FILTER_DUMMY_ANY_OPTION,
} from "@/constants/common.constants";
import { TimeFrame } from "@tajawal/web-flights-sdk/lib/types/fare-rules.types";
import {
  INSURANCE_DOMESTIC_BENEFITS,
  INSURANCE_INTERNATIONAL_BENEFITS,
  BAGGAGE_OPTION_ICONS,
  TRANSLATION_SEPARATOR,
  AIRCRAFT_ICONS_SRC,
  STOP_FILTER_OPTION_LABEL,
} from "@/constants/content.constants";
import {
  SelectFilterState,
  SelectFilter,
} from "@tajawal/web-flights-sdk/lib/types/filters/select-filter.types";

// constants
import { SORT_BY_MAP } from "@tajawal/web-flights-sdk/lib/constants/sort.constants";
import {
  FILTER_MAP,
  BAGGAGE_FILTER_CODE_MAP,
  TIME_FILTER_SEGMENTS,
  FLIGHT_LEG_TYPE_MAP,
  QUICK_FILTER_ID_MAP,
} from "@tajawal/web-flights-sdk/lib/constants/filters.constants";
import { FareCalendarPricingMapItem } from "@tajawal/web-flights-sdk/lib/types/fare-calendar.types";
import { BrandedFaresModel } from "@tajawal/web-flights-sdk/lib/types/fare-families.types";
import { buildTestId } from "@/utils/helpers.utils";
import { SearchRequestModel } from "@tajawal/web-flights-sdk/lib/types/search-request.types";
import { generateStopFilterTestID } from "@/utils/test-ids.utils";
import type {
  FilterCode,
  QuickFilter,
} from "@tajawal/web-flights-sdk/lib/types/filters/index.types";
import _isUndefined from "lodash/isUndefined";
import {
  DomesticItinerary,
  FareFamilyItinerary,
  ItineraryPrice,
} from "@tajawal/web-flights-sdk/lib/types/itinerary.types";
import _min from "lodash/min";
import { LegModel } from "@tajawal/web-flights-sdk/lib/types/leg.types";
import { PartnerBlockedPaymentMethods } from "@tajawal/web-flights-sdk/lib/types/loyalty-reward.types";
import type { CurrencyStore } from "@tajawal/web-core-sdk-v2";
import { utilFormatSARToRiyal } from "@/app-common/components/FormatSARToRiyalSymbol";
import { COLOR_VARIANTS } from "@tajawal/ct-web-design-system";

export function getAirportDisplayContent(airport = {}) {
  const code = _get(airport, "code", "");
  const city = _get(airport, "city", code);
  const country = _get(airport, "country", code);
  const label = _get(airport, "label", code);
  return `${city}, ${country} - ${label}`;
}

export function getTravellerType(
  code: string,
  t: TFunction,
  count?: number,
): string {
  switch (code) {
    case "ADT":
      return t("Common.Adult", { count: count || 1 });
    case "CH":
    case "CHD":
      return t("Common.Child", { count: count || 1 });
    case "INF":
      return t("Common.Infant", { count: count || 1 });
    case "ADT,CHD":
      return t("Common.Adult_Child");
    default:
      return "";
  }
}

export function getPerPersonText(
  t: TFunction,
  searchRequest: SearchRequestModel,
  withRoundBrackets?: boolean,
) {
  const { totalPax, paxList } = searchRequest;

  if (totalPax < 2 || _isNil(totalPax) || _isNil(paxList)) {
    return "";
  }

  if (paxList.includes(TRAVELLERS_TYPES_CODES_MAP.CHD)) {
    return withRoundBrackets
      ? `(${t("Baggage.Per_Adult_Child")})`
      : t("Baggage.Per_Adult_Child");
  }

  return withRoundBrackets
    ? `(${t("Baggage.Per_Adult")})`
    : t("Baggage.Per_Adult");
}

export function getLegStopsContent(stopCount, t) {
  const stopCode = stopCount > 2 ? "+2" : String(stopCount);
  return getStopLabelContent(stopCode, t);
}

export function getStopLabelContent(code, t) {
  switch (String(code)) {
    case "0":
      return t("SearchResults.SearchCard_DirectStop");
    case "1":
      return t("SearchResults.SearchCard_OneStop");
    case "2":
      return t("SearchResults.SearchCard_TwoStops");
    case "+2":
      return t("SearchResults.SearchCard_+2Stops");
    default:
      return "";
  }
}

export function getBaggageInfoLabelContent({
  isIncluded,
  hasBaggageOptions,
  hasFareFamilies,
  baggageOptionsAvailable,
  t,
}) {
  if (isIncluded) {
    return t("SearchResultLegCard.BaggageInfoLabel_Included");
  }

  if (hasBaggageOptions) {
    return t("SearchResultLegCard.BaggageInfoLabel_Options");
  }

  if (hasFareFamilies || baggageOptionsAvailable) {
    return t("SearchResultLegCard.BaggageInfoLabel_Options_Available");
  }

  return t("SearchResultLegCard.BaggageInfoLabel_NotIncluded");
}

export function getTravellerFormLabelContent({
  travellerValues = {} as TravellerModel,
}) {
  const { title, firstName = "", lastName = "", infantInLab } = travellerValues;
  const fullName = title && firstName && `${title} ${firstName} ${lastName}`;
  return {
    hasInfantInLab: infantInLab,
    fullName,
  };
}

export function getAdultOptionsContent(t) {
  return {
    Mr: { code: "Mr", label: t("PaxForm.Mr") },
    Ms: { code: "Ms", label: t("PaxForm.Ms") },
    Mrs: { code: "Mrs", label: t("PaxForm.Mrs") },
  };
}

export function getChildOptionsContent(t) {
  return {
    Mr: { code: "Mr", label: t("PaxForm.Master") },
    Ms: { code: "Ms", label: t("PaxForm.Miss") },
  };
}

export function getIdTypeLabelContent(code, t) {
  switch (code) {
    case "passport":
      return t("Pax.TravellerForm_PassportNumber");
    case "nationalId":
      return t("Pax.TravellerForm_GCCIDNumber");
    case "iqamaId":
      return t("Pax.TravellerForm_iqamaID");
    default:
      return "";
  }
}

export function getExpiryDateLabelContent(type, t) {
  return t("PaxForm.ExpireDate");
}

export function getCabinContent({
  cabin,
  t,
}: {
  cabin: string;
  t: any;
}): string {
  switch (cabin) {
    case CABIN_TYPES.ECONOMY:
      return t("Common.Cabin_Economy");
    case CABIN_TYPES.PREMIUM_ECONOMY:
      return t("Common.Cabin_PremiumEconomy");
    case CABIN_TYPES.BUSINESS:
      return t("Common.Cabin_Business");
    case CABIN_TYPES.FIRST_CLASS:
      return t("Common.Cabin_First");
    default:
      return "";
  }
}

export function getTravellerText({
  noOfTravellers,
  t,
}: {
  noOfTravellers: number;
  t: any;
}): string {
  return `${noOfTravellers} ${t("Common.travellers", {
    count: noOfTravellers,
  })}`;
}

export function getRecentSearchCardContent({
  recentItem,
  $resources,
  t,
}: {
  recentItem: Record<string, any>;
  $resources: FlightInfoStore["$resources"];
  t: any;
}) {
  const getAirportCity = $resources?.getAirportCity;
  const origin = getAirportCity(recentItem?.originId ?? "");
  const destination = getAirportCity(recentItem?.destinationId ?? "");
  const cabin = recentItem?.cabin ?? "";
  const noOfTravellers = recentItem?.pax ?? 0;
  const isRoundTrip = recentItem?.isRoundTrip ?? false;
  const searchType = isRoundTrip
    ? t("HomePage.RecentSearch_Roundtrip")
    : t("HomePage.RecentSearch_Oneway");
  const departureDate = recentItem?.departureDate ?? "";
  const returnDate = recentItem?.returnDate ?? "";
  const travellingDate = isRoundTrip
    ? `${formatDate(
        departureDate,
        DATE_FORMATS.RECENT_SEARCH_RESULTS,
      )} - ${formatDate(returnDate, DATE_FORMATS.RECENT_SEARCH_RESULTS)}`
    : formatDate(departureDate, DATE_FORMATS.RECENT_SEARCH_RESULTS);

  return {
    origin,
    destination,
    cabinClass: getCabinContent({ cabin, t }),
    noOfTravellers: getTravellerText({ noOfTravellers, t }),
    travellingDate,
    searchType,
  };
}

export function getTitleOptionsContent(
  t: TFunction,
  isAdult = true,
): Partial<
  Record<
    TravellerTitleLabel,
    { code: TravellerTitle; label: string; testId: string }
  >
> {
  return isAdult
    ? {
        Mr: { testId: "Mr", code: "Mr", label: t("Common.Title_Mr") },
        Ms: { testId: "Ms", code: "Ms", label: t("Common.Title_Ms") },
        Mrs: { testId: "Mrs", code: "Mrs", label: t("Common.Title_Mrs") },
      }
    : {
        Master: {
          testId: "Master",
          code: "Mr",
          label: t("Common.Title_Master"),
        },
        Miss: { testId: "Miss", code: "Ms", label: t("Common.Title_Miss") },
      };
}

export function getNameFromForm({
  form,
  fieldName,
  t,
  isAdult,
}: {
  form: FormApi;
  fieldName: (name: string) => string;
  t: TFunction;
  isAdult: boolean;
}) {
  const titleValue = _get(form.getState().values, fieldName("title"), "");
  const titleOptions = getTitleOptionsContent(t, isAdult);
  const title = _values(titleOptions).find(
    (value) => value.code === titleValue,
  )?.label;
  const firstName = _get(form.getState().values, fieldName("firstName"), "");
  const lastName = _get(form.getState().values, fieldName("lastName"), "");

  return `${title} ${firstName} ${lastName}`;
}

export function getExtraBaggageContent({
  extraBaggage,
  t,
}: {
  extraBaggage: LuggageOptionModel;
  t: TFunction;
}) {
  if (!_isPlainObject(extraBaggage)) return null;

  const primary = t("Pax.Baggage_ExtraContent_Primary", {
    weight: extraBaggage.weight,
  });

  const secondary = t("Pax.Baggage_ExtraContent_Secondary", {
    count: extraBaggage.quantity,
    quantity: extraBaggage.quantity,
    currency: extraBaggage.currency,
    displayAmount: extraBaggage.displayAmount,
  });

  return {
    primary,
    secondary,
  };
}

export const contactFieldName = (key: string): string => `contact.${key}`;

export function getContact(
  cartContact: Partial<Contact>,
  cachedContact: Partial<Contact>,
): Partial<Contact> {
  return {
    ...cachedContact,
    ...cartContact,
  };
}

export function getFormTitleContent({
  form,
  fieldName,
  traveller,
  t,
}: {
  form: FormApi;
  fieldName: (name: string) => string;
  traveller: TravellerModel;
  t: TFunction;
}): string {
  let formTitle = "";
  const { type, indexByType } = traveller;

  const titleValue = _get(form.getFieldState(fieldName("title")), "value");
  const titleOptions = getTitleOptionsContent(t);
  const title = _get(titleOptions[titleValue], "label");
  const firstName = _get(form.getFieldState(fieldName("firstName")), "value");
  const lastName = _get(form.getFieldState(fieldName("lastName")), "value");

  if (title && firstName) {
    formTitle += `${title} ${firstName}`;
  }

  if (formTitle && lastName) {
    formTitle += ` ${lastName}`;
  }

  if (!formTitle) {
    const typeContent = getTravellerType(type, t);

    formTitle = `${typeContent} ${indexByType + 1}`;
  }

  return formTitle;
}

export function getFieldIdFromName(fieldName: string): string {
  return _replace(fieldName, /\./g, "_");
}

export function getIdExpiryDateLabelContent(
  idType: string,
  t: TFunction,
): string {
  const idTypeLabel = getIdTypeLabelContent(idType, t);
  return t("Pax.TravellerForm_IdExpiryDateField_Label", {
    idType: idTypeLabel,
  });
}

export function getTravllerFormTitle(
  type: PaxType,
  index: number,
  t: TFunction,
): string {
  const paxType = getTravellerType(type, t);
  return t("Pax.TravellerForm_title", { type: paxType, sNo: index + 1 });
}

export function getTravllerFormButtonLabel(
  type: PaxType,
  index: number,
  t: TFunction,
): string {
  const paxType = getTravellerType(type, t);
  return t("Pax.TravellerForm_ButtonLabel", { type: paxType, sNo: index + 1 });
}

export function getPaxLabelByType(
  type: PaxType,
  index: number,
  t: TFunction,
): string {
  return `${getTravellerType(type, t)} ${index + 1}`;
}

export function getTravellerTypeCounterContent(
  travellers: TravellerModel[],
): string {
  if (_isPlainObject(travellers)) {
    return `1 adult`;
  }
  const travellersTypes = _countBy(travellers, "type");
  return `${_get(travellersTypes, "ADT", 0)} adults, ${_get(
    travellersTypes,
    "CH",
    0,
  )} child,
  ${_get(travellersTypes, "INF", 0)} infant`;
}

export function getCabinBaggageContent({
  t,
  baggage,
}: {
  t: TFunction;
  baggage: BaggageInfoModel;
}): Record<"primary" | "secondary", string> {
  if (!_isPlainObject(baggage) || !baggage.isIncluded)
    return { primary: t("Baggage.Cabin_NotIncluded"), secondary: "" };

  return {
    primary: t(`Baggage.CabinBag_V1`, {
      weight: baggage.allowance.toString(),
      count: baggage.qty as number,
      quantity: baggage.qty,
    }),
    secondary: t(`Baggage.CabinBag_Secondary_V1`, {
      count: baggage.qty as number,
      quantity: baggage.qty,
    }),
  };
}

export function getCheckedBaggageContent({
  t,
  baggage,
}: {
  t: TFunction;
  baggage: BaggageInfoModel;
}): Record<"primary" | "secondary", string> {
  const isBaggageNotIncluded =
    !_isPlainObject(baggage) ||
    !baggage.isIncluded ||
    !baggage?.quantity ||
    baggage?.quantity < 1;
  if (isBaggageNotIncluded) {
    return {
      primary: t("Baggage.Checked_NotIncluded"),
      secondary: "",
    };
  }

  const { quantity, totalWeight, perPieceWeight, perPieceWeightFlag } = baggage;

  const hasTotalWeight = _isNumber(totalWeight) && totalWeight;
  const primary = hasTotalWeight
    ? t(`Baggage.TotalWeight_V1`, {
        weight: totalWeight,
      })
    : t("BaggageInfo.CheckedBaggageIncluded");
  let secondary = t(`Baggage.BaggagePiece`, { count: quantity, quantity });

  if (perPieceWeightFlag) {
    switch (perPieceWeightFlag) {
      case PERPIECEWEIGHTFLAG.FIRST:
        secondary = t(`Baggage.MaxWeight_First_V1`, {
          quantity,
          maxWeight: perPieceWeight,
        });
        break;
      case PERPIECEWEIGHTFLAG.SPLIT:
        secondary = t(`Baggage.MaxWeight_Split_V1`, {
          quantity,
          maxWeight: perPieceWeight,
        });
        break;
      case PERPIECEWEIGHTFLAG.AVERAGE:
        secondary = t("Baggage.StaticWeightInfo_V1", {
          weight: perPieceWeight,
          quantity,
          count: quantity,
        });
        break;
      default:
    }
  }

  return {
    primary,
    secondary,
  };
}

export function getBaggageTitleByType(
  type: PaxType,
  index: number,
  t: TFunction,
): string {
  return `${t("Pax.BaggageSection_Title")} - ${getPaxLabelByType(
    type,
    index,
    t,
  )}`;
}

export function getEventLabel(page: string): string {
  if (page === APP_PAGES.FLIGHTS_PAX) {
    return "pax_details.origin";
  }
  if (page === APP_PAGES.FLIGHTS_PAYMENT) {
    return "payment_page.origin";
  }
  if (page === APP_PAGES.FLIGHTS_MANAGE_BOOKING) {
    return "booking_details.origin";
  }
}

export function getCancellationChangeMessage(
  timeFrame: TimeFrame,
  t: TFunction,
): string {
  const hasSameTimeFrame = _get(timeFrame, "hasSameTimeFrame", false);
  const cancellationTimeFrame = _get(timeFrame, "cancellationTimeFrame", false);
  const changeTimeFrame = _get(timeFrame, "changeTimeFrame", false);
  const timeFrameValue = _get(timeFrame, "commonTimeFrame", "");
  if (hasSameTimeFrame) {
    return t("FareRules.TimeFrame_Same", {
      timeFrameValue,
    });
  }
  if (cancellationTimeFrame && changeTimeFrame && !hasSameTimeFrame) {
    return t("FareRules.TimeFrame_Different", {
      changeTimeFrame,
      cancellationTimeFrame,
    });
  }
  if (cancellationTimeFrame && !changeTimeFrame && !hasSameTimeFrame) {
    return t("FareRules.TimeFrame_CancellationOnly", {
      cancellationTimeFrame,
    });
  }
  if (!cancellationTimeFrame && changeTimeFrame && !hasSameTimeFrame) {
    return t("FareRules.TimeFrame_ChangeOnly", {
      changeTimeFrame,
    });
  }
  return "";
}

export function getLogoImg(icon: string): string {
  return `${AIRLINE_IMAGE_URL}/${icon}.png`;
}

export function getLegOperatedByContent(
  ticketSoldBy: string,
  partiallyOperatedBy: string,
  t: TFunction,
): string {
  if (ticketSoldBy) {
    return t("FlightSummary.Flight_TicketSoldBy");
  }

  if (partiallyOperatedBy) {
    return t("FlightSummary.Flight_OperatedBy");
  }
  return "";
}

export function getTerminalName(
  code: string,
  terminalCode: string,
  t: TFunction,
): string {
  if (code === "JED" && terminalCode) {
    if (terminalCode === "N") {
      return t("FlightSummary.Terminal_North");
    }
    if (terminalCode === "S") {
      return t("FlightSummary.Terminal_South");
    }
    if (terminalCode === "1") {
      return t("FlightSummary.Terminal_New");
    }
    if (terminalCode === "h") {
      return t("FlightSummary.Terminal_Hajj");
    }
  } else {
    if (terminalCode) {
      return t("FlightSummary.Terminal", { code: terminalCode });
    }
    return "";
  }
}

export function getFareFamiliesFareRuleContent({
  isAvailable,
  withFees,
  isChangeRule,
  isCreditShell,
  t,
}: {
  isAvailable: boolean;
  withFees: boolean;
  isChangeRule: boolean;
  isCreditShell: boolean;
  t: TFunction;
}): string {
  if (isAvailable && withFees) {
    if (isChangeRule) {
      return isCreditShell
        ? t("FareRules.FlightChange_WithFees_Credit")
        : t("FareRules.FlightChange_WithFees");
    }
    return isCreditShell
      ? t("FareRules.Cancellation_WithFees_Credit")
      : t("FareRules.Cancellation_WithFees");
  }

  if (isAvailable && !withFees) {
    if (isChangeRule) {
      return isCreditShell
        ? t("FareRules.FlightChange_WithoutFees_Credit")
        : t("FareRules.FlightChange_WithoutFees");
    }
    return isCreditShell
      ? t("FareRules.Cancellation_WithoutFees_Credit")
      : t("FareRules.Cancellation_WithoutFees");
  }
  if (isChangeRule) {
    return isCreditShell
      ? t("FareRules.FlightChange_NotAvailable_Credit")
      : t("FareRules.FlightChange_NotAvailable");
  }
  return isCreditShell
    ? t("FareRules.Cancellation_NotAvailable_Credit")
    : t("FareRules.Cancellation_NotAvailable");
}

export function getLoginInfoContent({
  authState,
  userName,
  t,
}: {
  authState: string;
  userName: string;
  t: TFunction;
}): string {
  if (authState === AUTH_STATE_MAP.LOGGED_IN) {
    return t("Pax.SignIn_LoggedInWelcome", { userName });
  }

  const brand = t("Flights.Common_Almosafer");
  return t("Pax.SignIn_RegisteredWelcome", { brand });
}

export function getInsuranceBenefits(
  t: TFunction,
  isDomestic: boolean,
): InsuranceBenefits[] {
  const benefits = isDomestic
    ? INSURANCE_DOMESTIC_BENEFITS
    : INSURANCE_INTERNATIONAL_BENEFITS;

  return benefits.map((benefit) => ({
    title: t(`TripInsuarance.Benefits_${benefit}_Title`),
    coverage: t(`TripInsuarance.Benefits_${benefit}_Coverage`, {
      count: isDomestic ? 1 : 2,
    }),
    caption: t(`TripInsuarance.Benefits_${benefit}_Caption`, {
      defaultValue: "",
      count: isDomestic ? 1 : 2,
    }),
    logo: benefit,
  }));
}

export function getInsuranceTnc(t: TFunction): string[] {
  return new Array(7)
    .fill(null)
    .map((_, index) => t(`TripInsuarance.TermsAndCondition_Note${index + 1}`));
}

export function getPaxCTAContent(
  t: TFunction,
  isLoading: boolean,
  hasExtrasPage: boolean,
): string {
  if (isLoading) {
    return t("Common.Loading");
  }
  if (hasExtrasPage) {
    return t("Common.Continue");
  }
  return t("Pax.ProceedToPayment");
}

export function getExtrasCTAContent(
  t: TFunction,
  isLoading: boolean,
  isAnyAddonAdded: boolean,
): string {
  if (isLoading) {
    return t("Common.Loading");
  }
  if (isAnyAddonAdded) {
    return t("Pax.ProceedToPayment");
  }
  return t("Extras.SkipAndContinueToFinalDetails");
}

export function getRemoveMessage(accessKey: string, t: TFunction): string {
  switch (accessKey) {
    case ADDONS.ONLINECHECKIN:
      return t("Pax.OnlineCheckin_RemoveMessage");
    case ADDONS.CFAR:
      return t("Pax.CFAR_RemoveMessage");
    case ADDONS.INSURANCE:
      return t("Pax.FreeInsurance_RemoveMessage");
    default:
      return "";
  }
}

export function getDepartureReturnFlight(
  resources: FlightInfoStore["$resources"],
  departureCarrier: string,
  returnCarrier: string,
  isOneWay: boolean,
  t: TFunction,
): string {
  const departureAirline = resources.getAirlineLabel(departureCarrier);
  if (departureCarrier === returnCarrier || isOneWay) {
    return departureAirline;
  }
  return t("SearchResults.Multiple_Airlines");
}

export function getFareFamiliesBaggageContent({
  isIncluded,
  weight,
  isChecked,
  t,
  isUnderSeatBaggage,
  dimensions,
}: {
  isIncluded: boolean;
  weight: string;
  isChecked: boolean;
  t: TFunction;
  isUnderSeatBaggage?: boolean;
  dimensions?: string;
}): string {
  if (isUnderSeatBaggage && dimensions) {
    return t("Baggage.SmallUnderSeatBaggage", { dimensions });
  }

  const weightText = isIncluded
    ? _replace(weight, "KG", t("Flights.Common_KG"))
    : t("Flights.Common_Baggage_NoBaggages");
  return isChecked
    ? `${weightText} ${t("Flights.Common_Baggage_CheckedBaggages")}`
    : `${weightText} ${t("Flights.Common_Baggage_CabinBaggages")}`;
}

export function getFlightConditionDescription(
  combinedDescription: string,
): string[] {
  return combinedDescription.split(TRANSLATION_SEPARATOR);
}

export function getSortMenuOptions(
  state: SelectFilterState,
  currentStep: string,
): SelectFilterState {
  const excludedCodes = {
    [OUTBOUND]: [
      SORT_BY_MAP.EARLIST_ARRIVAL.key,
      SORT_BY_MAP.LATEST_ARRIVAL.key,
    ],
    [INBOUND]: [
      SORT_BY_MAP.EARLIST_DEPARTURE.key,
      SORT_BY_MAP.LATEST_DEPARTURE.key,
    ],
  };
  return _filter(
    state,
    ({ code }) => !excludedCodes[currentStep]?.includes(code),
  );
}

export function getSortItemContent(code: string, t: TFunction): string {
  switch (code) {
    case SORT_BY_MAP.BEST.key:
      return t("SortMenu.Recommended_Flights");
    case SORT_BY_MAP.CHEAPEST.key:
      return t("SortMenu.Lowest_Price");
    case SORT_BY_MAP.SHORTEST.key:
      return t("SortMenu.Shortest_Duration");
    case SORT_BY_MAP.EARLIST_DEPARTURE.key:
      return t("SortMenu.Earliest_Departure");
    case SORT_BY_MAP.LATEST_DEPARTURE.key:
      return t("SortMenu.Latest_Departure");
    case SORT_BY_MAP.EARLIST_ARRIVAL.key:
      return t("SortMenu.Earliest_Return");
    case SORT_BY_MAP.LATEST_ARRIVAL.key:
      return t("SortMenu.Latest_Return");
    default:
      return "";
  }
}

export function getBaggageMenuOptions(state: SelectFilter[], t: TFunction) {
  const OPTION_LABEL = {
    [String(BAGGAGE_FILTER_CODE_MAP.HAS_CHECKED_BAGGAGE)]: t(
      "BaggageOptions.Checked_Included",
    ),
  };
  const selecetAllOption = {
    code: FILTER_DUMMY_ANY_OPTION,
    label: t("BaggageOptions.Any_Option"),
    selected: false,
    value: FILTER_DUMMY_ANY_OPTION,
    isAvailable: true,
    testId: buildTestId("BaggageOptions", "AnyOption"),
  };
  return [selecetAllOption, ...state].map((option) => {
    return {
      ...option,
      label: option.label || OPTION_LABEL[String(option.code)],
      value: option.code,
      isDisabled: !option.isAvailable,
      testId: buildTestId("BaggageOptions", String(option.code)),
    };
  });
}

export function getBaggageOptionLabel(code: FilterCode, t: TFunction) {
  const OPTION_LABEL = {
    [String(BAGGAGE_FILTER_CODE_MAP.HAS_CHECKED_BAGGAGE)]: t(
      "BaggageOptions.Checked_Included",
    ),
    [String(BAGGAGE_FILTER_CODE_MAP.HAS_NOT_CHECKED_BAGGAGE)]: t(
      "BaggageOptions.Checked_Not_Included",
    ),
  };
  return OPTION_LABEL[String(code)];
}

export function getCabinMenuOptions(testIdPrefix: string, t: TFunction) {
  return _map(Object.values(CABIN_TYPES), (cabin, index) => ({
    label: getCabinContent({ cabin, t }),
    value: cabin,
    isDisabled: false,
    testId: buildTestId(testIdPrefix, `CabinMenu_option${index + 1}`),
  }));
}

export function getStopsMenuOptions(state: SelectFilter[], t: TFunction) {
  return _map(state, (option) => {
    return {
      ...option,
      label: option.label || t(STOP_FILTER_OPTION_LABEL[String(option.code)]),
      value: option.code,
      isDisabled: !option.isAvailable,
      testId: generateStopFilterTestID(option.code, option.isAvailable),
    };
  });
}

export function isBaggageIncluded(
  baggageInfo: LegBaggageInfo[],
  index: number,
) {
  return baggageInfo[index]?.freeBaggage?.checkIn?.isIncluded;
}

export function getBaggageIcon({
  index,
  baggageInfo,
  fareFamiliesBaggage,
  brandadedFare,
  isPaxPage = false,
}: {
  brandadedFare?: BrandedFaresModel;
  fareFamiliesBaggage: boolean;
  baggageInfo: LegBaggageInfo[];
  index: number;
  isPaxPage?: boolean;
}) {
  const hasIncludedBaggage = isBaggageIncluded(baggageInfo, index);
  const isBaggageOptionsAvailable =
    fareFamiliesBaggage ||
    baggageInfo[index]?.freeBaggage?.baggageOptionsAvailable;
  const isCheckInBaggageIncluded = brandadedFare?.checkInBaggage?.isIncluded;

  if (isCheckInBaggageIncluded || hasIncludedBaggage) {
    return BAGGAGE_OPTION_ICONS.INCLUDED;
  }

  if (isBaggageOptionsAvailable && !isPaxPage) {
    return BAGGAGE_OPTION_ICONS.AVAILABLE;
  }

  return BAGGAGE_OPTION_ICONS.NOT_INCLUDED;
}

export function getSingleSelectOptionsContent(
  id: string,
  state: SelectFilter[],
  t: TFunction,
) {
  // TODO: try to move the dummy options to SDK
  switch (id) {
    case FILTER_MAP.BAGGAGE:
      return getBaggageMenuOptions(state, t);
    case FILTER_MAP.STOP:
      return getStopsMenuOptions(state, t);
    default:
      return [];
  }
}

export function getSingleSelectLabelContent(id: string, t: TFunction) {
  switch (id) {
    case FILTER_MAP.BAGGAGE:
      return t("SearchResults.ActionBar_Baggage");
    case FILTER_MAP.STOP:
      return t("SearchResults.ActionBar_Stops");
    default:
      return "";
  }
}

export function getFiltersTabsContent({
  searchType,
  legCount,
  isRoundTrip,
  t,
}) {
  if (isRoundTrip) return [];
  switch (_toLower(searchType)) {
    case SEARCH_TYPES.MULTI_CITY:
      return _map(Array(legCount), (v, index) => ({
        code: index,
        label: `${t("Common.Flight")} ${index + 1}`,
      }));
    default:
      return [];
  }
}

export function getTimeFilterLabelContent(code, t) {
  switch (code) {
    case TIME_FILTER_SEGMENTS.AM.code:
      return t("Filters.Time_Morning");
    case TIME_FILTER_SEGMENTS.AM2.code:
      return t("Filters.Time_Midday");
    case TIME_FILTER_SEGMENTS.PM.code:
      return t("Filters.Time_Evening");
    case TIME_FILTER_SEGMENTS.PM2.code:
      return t("Filters.Time_Night");
    default:
      return "";
  }
}

export function getTime24HoursFilterContent(code, t) {
  switch (code) {
    case TIME_FILTER_SEGMENTS.AM.code:
      return t("Filters.Time24Hours_AM");
    case TIME_FILTER_SEGMENTS.AM2.code:
      return t("Filters.Time24Hours_AM2");
    case TIME_FILTER_SEGMENTS.PM.code:
      return t("Filters.Time24Hours_PM");
    case TIME_FILTER_SEGMENTS.PM2.code:
      return t("Filters.Time24Hours_PM2");
    default:
      return "";
  }
}

export function getTimeContent(isAMFormat, code, t) {
  if (isAMFormat) {
    return getTime12HoursFilterContent(code, t);
  }
  return getTime24HoursFilterContent(code, t);
}

export function getTime12HoursFilterContent(code, t) {
  switch (code) {
    case TIME_FILTER_SEGMENTS.AM.code:
      return t("Filters.Time12Hours_AM_V2");
    case TIME_FILTER_SEGMENTS.AM2.code:
      return t("Filters.Time12Hours_AM2_V2");
    case TIME_FILTER_SEGMENTS.PM.code:
      return t("Filters.Time12Hours_PM_V2");
    case TIME_FILTER_SEGMENTS.PM2.code:
      return t("Filters.Time12Hours_PM2_V2");
    default:
      return "";
  }
}

export function getFareCalendarDateContent(
  price: FareCalendarPricingMapItem,
  isRoundTrip: boolean,
): string {
  if (!price) return "";
  if (isRoundTrip) {
    return `${formatDate(
      new Date(price?.departureDate),
      DATE_FORMATS.FARE_CALENDAR_DATE_SHORT_FORMAT,
    )} - ${formatDate(
      new Date(price?.returnDate),
      DATE_FORMATS.FARE_CALENDAR_DATE_SHORT_FORMAT,
    )}`;
  }
  return formatDate(
    new Date(price?.departureDate),
    DATE_FORMATS.FARE_CALENDAR_DATE_LONG_FORMAT,
  );
}

export function getPriceChangeContent(priceUp: boolean, t: TFunction) {
  if (priceUp) {
    return t("PriceChangeContent.Up");
  }
  return t("PriceChangeContent.Down");
}

export function getTransportIcon(carrierType: string | number) {
  const aircraftType = AIRCRAFT_TYPE[carrierType] ? carrierType : "none";
  switch (carrierType) {
    case AIRCRAFT_TYPES.BUS:
      return AIRCRAFT_ICONS_SRC.BUS;
    case AIRCRAFT_TYPES.TRAIN:
      return AIRCRAFT_ICONS_SRC.TRAIN;
    case aircraftType:
      return AIRCRAFT_ICONS_SRC.AIRPLANE;
    default:
      return "";
  }
}

export function getPriceTagContent({
  hasFareFamilies,
  searchRequest,
  isGroup,
  direction,
  t,
}: {
  hasFareFamilies: boolean;
  isGroup: boolean;
  direction: FlightDirection;
  searchRequest: SearchRequestModel;
  t: TFunction;
}): { label: string; color: "aqua" | "grey"; type: "tint" | "filled" } {
  if (isGroup) {
    return {
      label: t("FlightCard.Price_TimeOptions"),
      color: "grey",
      type: "tint",
    };
  }
  const { isRoundTrip } = searchRequest;
  if (isRoundTrip) {
    if (direction === INBOUND) {
      return {
        label: hasFareFamilies
          ? t("SearchResults.SearchCard_AdditionalPrice_From")
          : t("SearchResults.SearchCard_AdditionalPrice"),
        color: "grey",
        type: "tint",
      };
    }
    return {
      label: t("SearchResults.SearchCard_RoundtripPrice"),
      color: "grey",
      type: "tint",
    };
  }
  if (hasFareFamilies) {
    return {
      label: t("FlightCard.Price_StartingFrom"),
      color: "grey",
      type: "tint",
    };
  }
}

export function getPriceContent({
  priceProps,
  isMobile,
  isPriceUpfrontActive = false,
}: {
  priceProps: ItineraryPrice<string>;
  isMobile?: boolean;
  isPriceUpfrontActive?: boolean;
}) {
  if (priceProps.roundtripTotal) {
    return {
      price:
        isMobile || isPriceUpfrontActive
          ? `+${priceProps.roundtripTotalDiff}`
          : priceProps.roundtripTotal,
      oldTotal: priceProps.oldRoundtripTotal,
      hasDiscount: priceProps.hasInboundDiscount,
    };
  }
  if (priceProps.cheapestRoundtripTotal) {
    return {
      price: priceProps.cheapestRoundtripTotal,
      oldTotal: priceProps.oldCheapestRoundtripTotal,
      hasDiscount: priceProps.hasOutboundDiscount,
    };
  }
  return {
    price: priceProps.total,
    oldTotal: priceProps.oldTotal,
    hasDiscount: priceProps.hasDiscount,
  };
}

export function getBaggageOfferText({ t, isCheckedIncluded, hasFareFamilies }) {
  if (isCheckedIncluded) {
    return null;
  }
  if (hasFareFamilies) {
    return {
      primary: t("Baggage.ExtraChecked"),
      secondary: "",
    };
  }
}

export function getTravellerContent(
  code: PaxType,
  number: number,
  t: TFunction,
): string {
  switch (code) {
    case "ADT":
      return t("Baggage.Adult", { number });
    case "CH":
      return t("Baggage.Child", { number });
    default:
      return "";
  }
}

export function getAddedBaggageContent(
  addedBaggage: BaggagePerTraveller,
  t: TFunction,
) {
  if (_isEmpty(addedBaggage)) {
    return {
      primary: "",
      secondary: "",
    };
  }
  const weight = _get(addedBaggage, "luggage.selectedOption.weight");
  const quantity = _get(addedBaggage, "luggage.selectedOption.quantity");
  const currency = _get(addedBaggage, "luggage.selectedOption.currency");
  const displayAmount = _get(
    addedBaggage,
    "luggage.selectedOption.displayAmount",
  );
  const indexByType = _get(addedBaggage, "indexByType", 0) + 1;

  const primary = t("Baggage.AddedBaggage_primary", {
    weight,
    quantity,
    currency,
    displayAmount,
  });

  return {
    primary,
    secondary: getTravellerContent(addedBaggage.type, indexByType, t),
  };
}

export function getUnderSeatBaggageContent({
  t,
  underSeatBaggage,
}: {
  t: TFunction;
  underSeatBaggage: BaggageInfoModel;
}): Record<"primary" | "secondary", string | null> {
  if (!_isPlainObject(underSeatBaggage) || !underSeatBaggage.isIncluded)
    return { primary: null, secondary: null };

  const { quantity, dimensions } = underSeatBaggage;
  const primary = t("Baggage.SmallUnderSeatBaggage", { dimensions });
  const secondary = t(`Baggage.BaggagePiece_V1`, { count: quantity, quantity });

  return {
    primary,
    secondary,
  };
}

export function getFareFamiliesUnderSeatBaggageContent({
  t,
  dimensions,
}: {
  t: TFunction;
  dimensions: string;
}): string {
  return t("Baggage.SmallUnderSeatBaggage", { dimensions });
}

export function getFareFamilyCheckinContent(
  timeBeforeDeparture: number,
  timeUnit: string,
  t: TFunction,
): string {
  if (timeUnit === DURATION_UNIT.DAY) {
    return t("FareFamilyCard.OnlineCheckinOpenTime_Days", {
      duration: timeBeforeDeparture,
      count: timeBeforeDeparture,
    });
  }

  return t("FareFamilyCard.OnlineCheckinOpenTime_Hours", {
    duration: timeBeforeDeparture,
    count: timeBeforeDeparture,
  });
}

export function getAddOnsBenefitsList(description: string): string[] {
  const list = _split(description, /<br(\s)?\/>|<br>|\\n|\\r/g);

  return _reduce(
    list,
    (benefits, listItem) => {
      const trimmedItem = _trim(listItem, " -");
      if (trimmedItem) {
        benefits.push(trimmedItem);
      }
      return benefits;
    },
    [],
  );
}

export function getAddonsBenefitsCTALabel(
  isLoading: boolean,
  isApplied: boolean,
  t,
): string {
  if (isApplied) {
    return isLoading ? t("Pax.AddOn_Removing") : t("Common.Remove");
  }
  return isLoading ? t("Pax.AddOn_Adding") : t("Pax.AddOn_Add");
}

export function getVATInfoContent({
  vatCountry,
  carrier,
  brand,
  isSA,
  isCOM,
  t,
}: {
  vatCountry: string;
  carrier: string;
  brand: string;
  isSA: boolean;
  isCOM: boolean;
  t: TFunction;
}): string {
  const isAlmosaferSaudi = (isSA || isCOM) && brand === BRANDS.ALMOSAFER;
  if (isAlmosaferSaudi && carrier === FLYADEAL_CODE) {
    return t("FlightDetails.F3VatInfoContent", {
      country: vatCountry,
      company: brand,
    });
  }

  return t("FlightDetails.VatInfoContent", {
    country: vatCountry,
    company: brand,
  });
}

export function getAirportTitle(
  name: string,
  type: string,
  resources: FlightInfoStore["$resources"],
  t: TFunction,
): string {
  const city = resources.getAirportCity(name);

  if (type === LOCATION_TYPES.TRAIN) {
    return t(`Airport.TrainTooltip`, { city });
  }
  if (type === LOCATION_TYPES.BUS) {
    return t(`Airport.BusTooltip`, { city });
  }

  return `${name}, ${city}, ${resources.getAirportCountry(
    name,
  )}, ${resources.getAirportLabel(name)}`;
}

export function getAirlineOperatedTooltipText(
  legCarriers: string[],
  resources: Partial<FlightInfoStore["$resources"]>,
): string {
  const carriersWithoutMain = legCarriers.slice(1);

  return _join(
    _flatMap(carriersWithoutMain, (carrier) =>
      resources.getAirlineLabel(carrier),
    ),
    ", ",
  );
}

export function isLoyalityProgramAvailable(
  loyalityProgram: string,
  blockedLoyaltyPrograms: PartnerBlockedPaymentMethods,
): boolean {
  return !blockedLoyaltyPrograms.some(
    (program) => program.paymentMethod === loyalityProgram,
  );
}

export function getLoyatyProgramsContent(
  {
    qitafMiles,
    fursanMiles,
    mokafaaMiles,
    shukranMiles,
  }: {
    qitafMiles: number;
    fursanMiles: number;
    mokafaaMiles: number;
    shukranMiles: number;
  },
  t: TFunction,
  blockedLoyaltyPrograms: PartnerBlockedPaymentMethods,
) {
  return [
    {
      shukranMiles,
      isAvailable:
        shukranMiles > 0 &&
        isLoyalityProgramAvailable(
          LOYALTY_METHODS.SHUKRAN,
          blockedLoyaltyPrograms,
        ),
      id: LOYALTY_METHODS.SHUKRAN,
      title: t("FareSummary.RewardsCard_Shukran"),
      description: [
        t("FareSummary.RewardsCard_Shukran_Description1"),
        t("FareSummary.RewardsCard_Shukran_Description2"),
      ],
    },
    {
      qitafMiles,
      isAvailable:
        qitafMiles > 0 &&
        isLoyalityProgramAvailable(
          LOYALTY_METHODS.QITAF,
          blockedLoyaltyPrograms,
        ),
      id: LOYALTY_METHODS.QITAF,
      title: t("FareSummary.RewardsCard_Qitaf"),
      description: [
        t("FareSummary.RewardsCard_Qitaf_Description1"),
        t("FareSummary.RewardsCard_Qitaf_Description2"),
        t("FareSummary.RewardsCard_Qitaf_Description3"),
      ],
    },
    {
      fursanMiles,
      isAvailable:
        fursanMiles > 0 &&
        isLoyalityProgramAvailable(
          LOYALTY_METHODS.FURSAN,
          blockedLoyaltyPrograms,
        ),
      id: LOYALTY_METHODS.FURSAN,
      title: t("FareSummary.RewardsCard_Alfursan"),
      description: [
        t("FareSummary.RewardsCard_Alfursan_Description1"),
        t("FareSummary.RewardsCard_Alfursan_Description2"),
      ],
    },
    {
      mokafaaMiles,
      isAvailable:
        mokafaaMiles > 0 &&
        isLoyalityProgramAvailable(
          LOYALTY_METHODS.MOKAFAA,
          blockedLoyaltyPrograms,
        ),
      id: LOYALTY_METHODS.MOKAFAA,
      title: t("FareSummary.RewardsCard_Mokafaa"),
      description: [
        t("FareSummary.RewardsCard_Mokafaa_Description1"),
        t("FareSummary.RewardsCard_Mokafaa_Description2"),
        t("FareSummary.RewardsCard_Mokafaa_Description3"),
      ],
    },
  ];
}

export function getStepperContent(t: TFunction): string[] {
  return [
    t("Pax.Stepper_FirstStep"),
    t("Pax.Stepper_SecondStep"),
    t("Pax.Stepper_ThirdStep"),
    t("Pax.Stepper_FourthStep"),
  ];
}

export function getStopInfoContent(segmentsWithTechnicalStops, $resources, t) {
  return _map(segmentsWithTechnicalStops, (segment) => {
    const { departureCode, duration, layover } = segment;
    const hasTechnicalStop = _get(segment, "isTechnicalStop", false);
    const technicalStops = _get(segment, "technicalStops", []);

    const waitingTime = hasTechnicalStop ? duration : layover;
    const waitingAirport = $resources.getAirport(
      hasTechnicalStop ? technicalStops[0].airport : departureCode,
    );
    const airportCode = hasTechnicalStop
      ? technicalStops[0].airport
      : departureCode;

    if (!waitingTime) {
      return null;
    }
    let city = "undefined";
    if (
      !_isUndefined(waitingAirport.city) &&
      waitingAirport.city !== "undefined"
    ) {
      city = waitingAirport.city;
    }

    return {
      mainText: t("SearchResults.StopsInfoTooltip_Waiting_Time"),
      waitingTime,
      airport: `${airportCode}, ${city}`,
    };
  });
}

export function getFareRulesInfoTitles(t: TFunction, index) {
  const fareRulesInfoContent = [
    t("FareRules.FareRulesInfo_CancellationFees"),
    t("FareRules.FareRulesInfo_FareDifference"),
    t("FareRules.FareRulesInfo_MissedFlight"),
    t("FareRules.FareRulesInfo_ServiceFee"),
  ];

  return fareRulesInfoContent[index];
}

export function getQuickFilterLabel({
  quickFilter,
  t,
  isAMFormat,
  requestAirports,
  $resources,
  color,
}: {
  quickFilter: QuickFilter;
  t: TFunction;
  isAMFormat: boolean;
  requestAirports: string[][];
  $resources: any;
  color: COLOR_VARIANTS;
}) {
  const { filter, payload, label, id } = quickFilter;
  const legType = payload?.type;
  const isOrigin = legType === FLIGHT_LEG_TYPE_MAP.ORIGIN;

  switch (filter) {
    case FILTER_MAP.STOP:
      return getStopFilterLabel(id, payload, t);
    case FILTER_MAP.BAGGAGE:
      return t("BaggageOptions.Checked_Included");
    case FILTER_MAP.AIRPORT:
      return getAirportFilterLabel(isOrigin, label, t);
    case FILTER_MAP.TIME:
      return getTimeFilterLabel(
        isOrigin,
        payload,
        requestAirports,
        isAMFormat,
        t,
        $resources,
      );
    case FILTER_MAP.PRICE:
      return getPriceFilterLabel(payload, t, color);
    default:
      return label;
  }
}

function getStopFilterLabel(id: string, payload: any, t: TFunction): string {
  return id === QUICK_FILTER_ID_MAP.DIRECT_ONLY
    ? t("Filters.Stops_DirectOnly")
    : t(STOP_FILTER_OPTION_LABEL[payload]);
}

function getAirportFilterLabel(
  isOrigin: boolean,
  label: string,
  t: TFunction,
): string {
  const action = isOrigin ? "FlyFrom" : "Arrivein";
  return `${t(`Filters.${action}`)} ${label}`;
}

function getTimeFilterLabel(
  isOrigin: boolean,
  payload: any,
  requestAirports: string[][],
  isAMFormat: boolean,
  t: TFunction,
  $resources: any,
): string {
  const { index, code } = payload || {};
  const action = isOrigin ? "FlyFrom" : "Arrivein";
  const city = $resources.getAirportCity(
    _get(requestAirports, `${index}.${isOrigin ? 0 : 1}`, ""),
  );
  return `${t(`Filters.${action}`)} ${city} ${getTimeContent(
    isAMFormat,
    code,
    t,
  )}`;
}

function getPriceFilterLabel(
  payload: any,
  t: TFunction,
  color: COLOR_VARIANTS,
) {
  return utilFormatSARToRiyal(
    `${t("Filters.Price_UpTo")} ${payload.currency} ${payload.to}`,
    color,
  );
}

export function getSpecialAssistanceContent(t: TFunction) {
  return [
    { value: "BSCT", label: t("Pax.SpecialService_BSCT") },
    {
      value: "DEAF",
      label: t("Pax.SpecialService_DEAF"),
    },
    { value: "WCOB", label: t("Pax.SpecialService_WCOB") },
    { value: "WCHR", label: t("Pax.SpecialService_WCHR") },
    {
      value: "WCHC",
      label: t("Pax.SpecialService_WCHC"),
    },
    { value: "WCHS", label: t("Pax.SpecialService_WCHS") },
    {
      value: "MAAS",
      label: t("Pax.SpecialService_MAAS"),
    },
  ];
}

export function getMealOptionContent(t: TFunction) {
  return [
    { value: "AVML", label: t("Pax.SpecialService_AVML") },
    { value: "BBML", label: t("Pax.SpecialService_BBML") },
    { value: "BLML", label: t("Pax.SpecialService_BLML") },
    { value: "CHML", label: t("Pax.SpecialService_CHML") },
    { value: "DBML", label: t("Pax.SpecialService_DBML") },
    { value: "FPML", label: t("Pax.SpecialService_FPML") },
    { value: "GFML", label: t("Pax.SpecialService_GFML") },
    { value: "HNML", label: t("Pax.SpecialService_HNML") },
    { value: "JPML", label: t("Pax.SpecialService_JPML") },
    { value: "KSML", label: t("Pax.SpecialService_KSML") },
    { value: "LCML", label: t("Pax.SpecialService_LCML") },
    { value: "LFML", label: t("Pax.SpecialService_LFML") },
    { value: "LSML", label: t("Pax.SpecialService_LSML") },
    { value: "MOML", label: t("Pax.SpecialService_MOML") },
    { value: "NFML", label: t("Pax.SpecialService_NFML") },
    { value: "NLML", label: t("Pax.SpecialService_NLML") },
    { value: "OBML", label: t("Pax.SpecialService_OBML") },
    { value: "RVML", label: t("Pax.SpecialService_RVML") },
    { value: "SFML", label: t("Pax.SpecialService_SFML") },
    { value: "SPML", label: t("Pax.SpecialService_SPML") },
    { value: "VGML", label: t("Pax.SpecialService_VGML") },
    { value: "VJML", label: t("Pax.SpecialService_VJML") },
    { value: "VOML", label: t("Pax.SpecialService_VOML") },
    { value: "VLML", label: t("Pax.SpecialService_VLML") },
  ];
}

export const getDisplayPriceContent = (
  displayPrice: ItineraryPrice<string>,
): { total: string } => {
  if (displayPrice?.roundtripTotal) {
    return { total: displayPrice.roundtripTotal };
  }
  if (displayPrice?.cheapestRoundtripTotal) {
    return { total: displayPrice.cheapestRoundtripTotal };
  }
  return { total: displayPrice?.total };
};

export const getFareFamilyPrice = (
  fareFamily: FareFamilyItinerary,
  isPriceUpFrontActive: boolean,
  isMobile?: boolean,
): string => {
  if (fareFamily?.displayPrice?.roundtripTotal) {
    return isMobile || isPriceUpFrontActive
      ? `+${fareFamily.displayPrice.fareFamilyRoundtripDifference}`
      : fareFamily.displayPrice.roundtripTotal;
  }
  if (fareFamily?.displayPrice?.cheapestRoundtripTotal) {
    return isMobile || isPriceUpFrontActive
      ? `+${fareFamily.displayPrice.fareFamilyRoundtripDifference}`
      : fareFamily.displayPrice.cheapestRoundtripTotal;
  }
  return fareFamily.displayPrice.total;
};

export const getDisplayPriceContentVariant = (
  itinerary: DomesticItinerary,
  displayItinerary: DomesticItinerary,
  $currency: CurrencyStore,
): string => {
  return $currency.formatPriceWithDecimals(
    (itinerary?.price?.roundtripTotal || 0) +
      (displayItinerary?.outboundItinerary?.price
        ?.fareFamilyRoundtripDifference || 0) +
      (displayItinerary?.inboundItinerary?.price
        ?.fareFamilyRoundtripDifference || 0),
    0,
  );
};

export const getArabicTranslationContent = (
  adults: number,
  children: number,
  infants: number,
  cabinText: string,
  language: string,
  t: TFunction,
) => {
  const count = _max([
    +adults + +children + +infants,
    SEARCH_BOX_CONSTANTS.MIN_ADULTS_COUNT,
  ]);
  const addedTranslation = language === "ar" ? `_${_min([count, 5])}` : "";

  return `${t(`HomePage.PassengerField_Count${addedTranslation}`, {
    count,
  })}, ${cabinText}`;
};

export function getFareRuleLabel({
  $resources,
  leg,
  t,
}: {
  $resources: FlightInfoStore["$resources"];
  leg: LegModel;
  t: TFunction;
}): string {
  return `${$resources.getAirportCity(leg.originId)} ${t(
    "Common.To",
  )} ${$resources.getAirportCity(
    leg.destinationId,
  )} - (${$resources.getAirlineLabel(leg.validatingCarrier)})`;
}

export const getPriceTagLabel = ({
  isRoundTrip,
  isInbound,
  hasFareFamilies,
  t,
}: {
  isRoundTrip: boolean;
  isInbound: boolean;
  hasFareFamilies: boolean;
  t: TFunction;
}): string => {
  if (!isRoundTrip) {
    return t("SearchResults.SearchCard_StartFrom");
  }
  if (isInbound && !hasFareFamilies) {
    return t("SearchResults.SearchCard_TotalRoundtripPrice");
  }

  return t("SearchResults.SearchCard_RoundtripPrice");
};

export function getBaggageTitle({
  originId,
  destinationId,
  $resources,
  carrier,
  isMultiPNR,
  t,
}: {
  originId: string;
  destinationId: string;
  $resources: FlightInfoStore["$resources"];
  carrier: string;
  isMultiPNR: boolean;
  t: TFunction;
}): string {
  const departure = $resources.getAirportCity(originId);
  const arrival = $resources.getAirportCity(destinationId);
  const airlinetext = isMultiPNR
    ? ` - ${$resources.getAirlineLabel(carrier)}`
    : "";
  return `${t("Baggage.FlightRoute", {
    from: departure,
    to: arrival,
  })}${airlinetext}`;
}

export const getTicktedByContent = (leg: LegModel): string => {
  if (leg.ticketSoldBy) {
    return leg.ticketSoldBy;
  }
  if (_size(leg.legSummary?.legCarriers) > 1) {
    return leg.validatingCarrier;
  }
};

export const getFilterCityIndex = (isStepByStep, currentStep) => {
  return {
    flyFrom: isStepByStep && currentStep === INBOUND ? 1 : 0,
    arriveIn: isStepByStep && currentStep === INBOUND ? 0 : 1,
  };
};

export function getBrandText(language = "en") {
  return { en: "Almosafer", ar: "المسافر" }[language];
}

export function getFlightDetailsHeaderCTALabel({
  isVariantA,
  isExpanded,
  t,
}: {
  isVariantA: boolean;
  isExpanded: boolean;
  t: TFunction;
}) {
  if (isVariantA) {
    return isExpanded
      ? t("FlightDetails.Hide_Details")
      : t("FlightDetails.Show_Details");
  }

  return isExpanded ? t("Common.Hide") : t("Common.Details");
}
